import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, User, CheckCircle, AlertCircle, Info, X, Settings } from 'lucide-react';

const Header = ({ title, subtitle, stats }) => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [userName, setUserName] = useState('Admin');
  const notifRef = useRef(null);

  useEffect(() => {
    // Get Gmail email for display
    const savedEmail = localStorage.getItem('gmailEmail');
    if (savedEmail) {
      // Extract name from email (part before @)
      const name = savedEmail.split('@')[0];
      setUserName(name.charAt(0).toUpperCase() + name.slice(1));
    }

    // Get dismissed notifications from localStorage
    const dismissedNotifs = JSON.parse(localStorage.getItem('dismissedNotifications') || '[]');

    // Generate sample notifications based on system status
    const notifs = [];
    
    if (stats) {
      // Notification for received emails (only if not dismissed)
      if (stats.received > 0 && !dismissedNotifs.includes('emails-ready')) {
        notifs.push({
          id: 'emails-ready',
          type: 'info',
          title: 'Emails Ready',
          message: `${stats.received.toLocaleString()} emails ready for replies`,
          time: 'Now',
          icon: Info
        });
      }
      
      // Notification for sent emails (only if not dismissed and sent count changed)
      const lastSentCount = localStorage.getItem('lastSentCount');
      if (stats.sent > 0 && stats.sent !== parseInt(lastSentCount) && !dismissedNotifs.includes(`emails-sent-${stats.sent}`)) {
        notifs.push({
          id: `emails-sent-${stats.sent}`,
          type: 'success',
          title: 'Emails Sent',
          message: `${stats.sent} emails sent successfully`,
          time: 'Today',
          icon: CheckCircle
        });
        // Update last sent count
        localStorage.setItem('lastSentCount', stats.sent.toString());
      }
    }

    // Add setup notification only if Gmail not connected and not dismissed
    const isConnected = localStorage.getItem('gmailEmail');
    if (!isConnected && !dismissedNotifs.includes('setup-required')) {
      notifs.push({
        id: 'setup-required',
        type: 'warning',
        title: 'Setup Required',
        message: 'Connect Gmail in Settings to start',
        time: 'Now',
        icon: AlertCircle
      });
    }

    setNotifications(notifs);
  }, [stats]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notifRef.current && !notifRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const clearNotification = (id) => {
    // Remove from current state
    setNotifications(notifications.filter(n => n.id !== id));
    
    // Save to localStorage so it doesn't show again
    const dismissedNotifs = JSON.parse(localStorage.getItem('dismissedNotifications') || '[]');
    if (!dismissedNotifs.includes(id)) {
      dismissedNotifs.push(id);
      localStorage.setItem('dismissedNotifications', JSON.stringify(dismissedNotifs));
    }
  };

  const clearAll = () => {
    // Get all notification IDs
    const allIds = notifications.map(n => n.id);
    
    // Save all to localStorage
    const dismissedNotifs = JSON.parse(localStorage.getItem('dismissedNotifications') || '[]');
    allIds.forEach(id => {
      if (!dismissedNotifs.includes(id)) {
        dismissedNotifs.push(id);
      }
    });
    localStorage.setItem('dismissedNotifications', JSON.stringify(dismissedNotifs));
    
    // Clear from state
    setNotifications([]);
  };

  return (
    <div className="bg-white border-b border-gray-200 px-8 py-4 shadow-sm">
      <div className="flex items-center justify-between">
        {/* Page Title */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
          {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
        </div>

        {/* Stats & Actions */}
        <div className="flex items-center gap-6">
          {/* Stats */}
          {stats && (
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 rounded-lg border border-blue-200">
                <span className="text-lg font-bold text-blue-600">
                  {stats.received?.toLocaleString() || 0}
                </span>
                <span className="text-xs font-medium text-gray-600">Received</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 rounded-lg border border-green-200">
                <span className="text-lg font-bold text-green-600">
                  {stats.sent || 0}
                </span>
                <span className="text-xs font-medium text-gray-600">Sent</span>
              </div>
            </div>
          )}

          {/* Notifications */}
          <div className="relative" ref={notifRef}>
            <button 
              onClick={() => setShowNotifications(!showNotifications)}
              className="relative p-2 hover:bg-gray-100 rounded-lg transition-colors group"
              title="Notifications"
            >
              <Bell className="h-5 w-5 text-gray-600 group-hover:text-gray-900" />
              {notifications.length > 0 && (
                <span className="absolute -top-1 -right-1 h-5 w-5 bg-red-500 rounded-full flex items-center justify-center text-[10px] font-bold text-white animate-pulse">
                  {notifications.length > 9 ? '9+' : notifications.length}
                </span>
              )}
            </button>

            {/* Notifications Dropdown */}
            {showNotifications && (
              <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-2xl border border-gray-200 z-50 max-h-96 overflow-hidden">
                {/* Header */}
                <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-blue-50 to-purple-50">
                  <h3 className="font-semibold text-gray-900">Notifications</h3>
                  {notifications.length > 0 && (
                    <button
                      onClick={clearAll}
                      className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                    >
                      Clear All
                    </button>
                  )}
                </div>

                {/* Notifications List */}
                <div className="max-h-80 overflow-y-auto">
                  {notifications.length > 0 ? (
                    notifications.map((notif) => {
                      const Icon = notif.icon;
                      const colors = {
                        success: 'bg-green-50 text-green-600 border-green-200',
                        warning: 'bg-orange-50 text-orange-600 border-orange-200',
                        info: 'bg-blue-50 text-blue-600 border-blue-200'
                      };

                      return (
                        <div
                          key={notif.id}
                          className="px-4 py-3 border-b border-gray-100 hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex items-start gap-3">
                            <div className={`p-2 rounded-lg ${colors[notif.type]}`}>
                              <Icon className="h-4 w-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-gray-900">{notif.title}</p>
                              <p className="text-xs text-gray-600 mt-1">{notif.message}</p>
                              <p className="text-xs text-gray-400 mt-1">{notif.time}</p>
                            </div>
                            <button
                              onClick={() => clearNotification(notif.id)}
                              className="text-gray-400 hover:text-gray-600"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="px-4 py-8 text-center">
                      <Bell className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                      <p className="text-sm text-gray-500">No notifications</p>
                      <p className="text-xs text-gray-400 mt-1">You're all caught up!</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* User */}
          <button
            onClick={() => navigate('/settings')}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-200 hover:border-purple-400 hover:shadow-md transition-all cursor-pointer group"
            title="Go to Settings"
          >
            <div className="h-8 w-8 rounded-full bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center group-hover:scale-110 transition-transform">
              <User className="h-4 w-4 text-white" />
            </div>
            <span className="text-sm font-semibold text-gray-700 group-hover:text-gray-900">{userName}</span>
            <Settings className="h-3 w-3 text-purple-600 opacity-0 group-hover:opacity-100 transition-opacity" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Header;

